import { useLazyQuery, useMutation } from '@apollo/client';
import { Delta } from 'quill/core';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';

const UPDATE_POST_CONTENT = gql(/** @complexity 2 */ `
  mutation updatePostContent(
    $id: ID!
    $delta: Json!
    $version: Int!
    $checksum: Int!
  ) {
    post: updatePostContent(
      id: $id
      delta: $delta
      version: $version
      checksum: $checksum
    ) {
      id
    }
  }
`);

const POST_CONTENT_QUERY = gql(/** @complexity 3 */ `
  query PostHistoryContent($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      content(isLatest: true) {
        id
        delta
        version
      }
      ...postPath_post
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
        delta
      }
    }
  }
`);

interface RestoreVersionOptions {
  postId: string;
  versionFrom: number;
  versionTo: number;
}

const useRestoreVersion = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loadPoastContent] = useLazyQuery(POST_CONTENT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [updatePostContent] = useMutation(UPDATE_POST_CONTENT);

  async function restore({
    postId,
    versionFrom,
    versionTo,
  }: RestoreVersionOptions) {
    try {
      const { data } = await loadPoastContent({
        variables: { postId, versionFrom, versionTo },
      });
      const post = data?.post;
      if (!post?.history) return;

      const currentDelta = new Delta(post.content.delta);
      const targetDelta = new Delta(post.history.snapshot).compose(
        new Delta(post.history.delta),
      );

      const diff = currentDelta.diff(targetDelta);

      await updatePostContent({
        variables: {
          id: postId,
          delta: JSON.stringify(diff),
          version: post.content.version,
          checksum: currentDelta.length(),
        },
      });
      navigate(postPath(post));
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Restored your post!',
        }),
      );
    } catch (e) {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, something went wrong. Please try again or contact support.',
        }),
      );
      throw e;
    }
  }

  return restore;
};

export default useRestoreVersion;
